import dayjs from "dayjs";

import { SelectDropdownOption } from "../components/APSelectDropDown";
import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { VehiclePricingRequestSearchInputs } from "../quote/searchVehiclePricingRequests";
import { Quote } from "../types/Quote";
import {
  VehiclePricingRequest,
  VehiclePricingRequestAggregations,
  VehiclePricingRequestSearchFilters,
  VehiclePricingRequestSearchSortOptions,
} from "../types/VehiclePricingRequest";
import {
  getAverageForObjectArray,
  getMinMaxForObjectArray,
  sortBy,
} from "./array";
import { currentDate, subtractDate } from "./dates";
import {
  addAutoCompleteOptionToParams,
  addDateFilterToParams,
  addStringFilterToParams,
  getAutoCompleteOptionFromParam,
  getSearchFilterFromParam,
} from "./search";

export type VehiclePricingRequestSearchSortOption =
  | "onroadPriceAsc"
  | "pricingReceivedDateDesc"
  | "discountDesc"
  | "selectedDesc";

export const VehiclePricingRequestSearchSortOptionsList: SelectDropdownOption<VehiclePricingRequestSearchSortOption>[] =
  [
    { id: "onroadPriceAsc", label: "Onroad Price" },
    { id: "pricingReceivedDateDesc", label: "Pricing Received Date" },
    { id: "discountDesc", label: "Discount" },
    { id: "selectedDesc", label: "Selected" },
  ];

function getSortFromParams(
  params: URLSearchParams
): Partial<
  Record<keyof VehiclePricingRequestSearchSortOptions, "asc" | "desc">
> {
  const param = params.get("sort") as VehiclePricingRequestSearchSortOption;

  if (!param) {
    return {
      onroadPrice: "desc",
    };
  }

  switch (param) {
    case "onroadPriceAsc":
      return { onroadPrice: "asc" };
    case "pricingReceivedDateDesc":
      return { pricingReceivedDate: "desc" };
    case "discountDesc":
      return { discount: "desc" };
    case "selectedDesc":
      return { selected: "desc" };
  }
}

// URL Search Params -> API Search Filters
export function buildVehiclePricingRequestSearchQueryFromParams(
  params: URLSearchParams,
  pageSize: number = 15
): VehiclePricingRequestSearchFilters {
  return {
    filters: {
      pricingReceived: {
        type: "eq",
        value: "true",
      },
      onroadPrice: getSearchFilterFromParam(params, "onroadPrice"),
      pricingReceivedDate: getSearchFilterFromParam(
        params,
        "pricingReceivedDate",
        true
      ),
      selected: getSearchFilterFromParam(params, "pricingSelected", true),
      state: getSearchFilterFromParam(params, "dealerState", true),
      year: getSearchFilterFromParam(params, "vehicleYear", true),
      make: getSearchFilterFromParam(params, "vehicleMake", true),
      model: getSearchFilterFromParam(params, "vehicleModel", true),
      variant: getSearchFilterFromParam(params, "vehicleVariant", true),
    },
    sort: getSortFromParams(params),
    pageSize: pageSize,
    nextPageLink: undefined,
  };
}

// Form Input -> URL Search Params
export function buildVehiclePricingRequestSearchParams(
  input: VehiclePricingRequestSearchInputs
): URLSearchParams {
  const newParams = new URLSearchParams();

  if (input.vehicleMake) {
    addAutoCompleteOptionToParams(newParams, "vehicleMake", [
      input.vehicleMake,
    ]);
  }
  if (input.vehicleModel) {
    addAutoCompleteOptionToParams(newParams, "vehicleModel", [
      input.vehicleModel,
    ]);
  }
  if (input.vehicleYear) {
    addAutoCompleteOptionToParams(newParams, "vehicleYear", [
      input.vehicleYear,
    ]);
  }
  if (input.vehicleVariant) {
    addAutoCompleteOptionToParams(newParams, "vehicleVariant", [
      input.vehicleVariant,
    ]);
  }
  if (input.pricingReceivedDateFrom) {
    addDateFilterToParams(
      newParams,
      "pricingReceivedDate",
      input.pricingReceivedDateFrom,
      "gte"
    );
  }

  if (input.maxOnroadPrice) {
    addStringFilterToParams(
      newParams,
      "onroadPrice",
      input.maxOnroadPrice,
      "lte"
    );
  }

  if (input.dealerState) {
    addAutoCompleteOptionToParams(newParams, "dealerState", [
      input.dealerState,
    ]);
  }

  if (input.pricingSelected) {
    addAutoCompleteOptionToParams(newParams, "pricingSelected", [
      input.pricingSelected,
    ]);
  }

  return newParams;
}

export function getDefaultVehiclePricingRequestSearchParams(
  quote: Quote
): URLSearchParams {
  const params = buildVehiclePricingRequestSearchParams({
    vehicleMake: {
      id: quote.vehicle.makeId,
      label: quote.vehicle.make,
    },
    vehicleModel: {
      id: quote.vehicle.modelId,
      label: quote.vehicle.model,
    },
    vehicleYear: {
      id: quote.vehicle.year,
      label: quote.vehicle.year,
    },
    vehicleVariant: {
      id: quote.vehicle.variantId,
      label: quote.vehicle.variantName,
    },
    dealerState: {
      id: quote.vehicle.state,
      label: quote.vehicle.state,
    },
    pricingSelected: {
      id: "all",
      label: "All",
    },
    pricingReceivedDateFrom: dayjs(
      subtractDate(currentDate(), 6, "month")
    ).toISOString(),
    maxOnroadPrice: "",
  });

  params.set("sort", "onroadPriceAsc");

  return params;
}

// URL Search Params -> Form Input
export type VehiclePricingRequestSearchInputOptions = {
  vehicleMakes: AutoCompleteOption[];
  vehicleModels: AutoCompleteOption[];
  vehicleYears: AutoCompleteOption[];
  vehicleVariants: AutoCompleteOption[];
  state: AutoCompleteOption[];
  pricingSelected: AutoCompleteOption[];
};

export function buildVehiclePricingRequestSearchInputsFromParams(
  params: URLSearchParams,
  inputOptions: VehiclePricingRequestSearchInputOptions
): VehiclePricingRequestSearchInputs {
  const inputs = {
    vehicleMake:
      getAutoCompleteOptionFromParam(
        params,
        "vehicleMake",
        inputOptions.vehicleMakes
      ) ?? null,
    vehicleModel:
      getAutoCompleteOptionFromParam(
        params,
        "vehicleModel",
        inputOptions.vehicleModels
      ) ?? null,
    vehicleYear:
      getAutoCompleteOptionFromParam(
        params,
        "vehicleYear",
        inputOptions.vehicleYears
      ) ?? null,
    vehicleVariant:
      getAutoCompleteOptionFromParam(
        params,
        "vehicleVariant",
        inputOptions.vehicleVariants
      ) ?? null,
    dealerState:
      getAutoCompleteOptionFromParam(
        params,
        "dealerState",
        inputOptions.state
      ) ?? null,
    pricingReceivedDateFrom: subtractDate(currentDate(), 6, "month"),
    maxOnroadPrice: "",
    pricingSelected:
      inputOptions.pricingSelected.find((option) => option.id === "all") ||
      null,
  };

  return inputs;
}

// Local Sort
export const sortVehiclePricingRequests = (
  requests: VehiclePricingRequest[],
  quoteSortOptionId: VehiclePricingRequestSearchSortOption
): VehiclePricingRequest[] => {
  const requestsToSort = [...requests];

  let compareFunction: (
    a: VehiclePricingRequest,
    b: VehiclePricingRequest
  ) => number;

  switch (quoteSortOptionId) {
    case "onroadPriceAsc":
      compareFunction = (
        a: VehiclePricingRequest,
        b: VehiclePricingRequest
      ) => {
        if ((a.onroadPrice ?? 0) < (b.onroadPrice ?? 0)) {
          return -1;
        }
        if ((a.onroadPrice ?? 0) > (b.onroadPrice ?? 0)) {
          return 1;
        }
        return 0;
      };
      break;
    case "pricingReceivedDateDesc":
      compareFunction = (
        a: VehiclePricingRequest,
        b: VehiclePricingRequest
      ) => {
        if ((a.pricingReceivedDate ?? "") > (b.pricingReceivedDate ?? "")) {
          return -1;
        }
        if ((a.pricingReceivedDate ?? "") < (b.pricingReceivedDate ?? "")) {
          return 1;
        }
        return 0;
      };
      break;
    case "discountDesc":
      compareFunction = (
        a: VehiclePricingRequest,
        b: VehiclePricingRequest
      ) => {
        if ((a.discount ?? 0) > (b.discount ?? 0)) {
          return -1;
        }
        if ((a.discount ?? 0) < (b.discount ?? 0)) {
          return 1;
        }
        return 0;
      };
      break;
    case "selectedDesc":
      compareFunction = (
        a: VehiclePricingRequest,
        b: VehiclePricingRequest
      ) => {
        if (a.selected && !b.selected) {
          return -1;
        }
        if (!a.selected && b.selected) {
          return 1;
        }
        return 0;
      };
      break;
  }

  requestsToSort.sort(compareFunction);

  return requestsToSort;
};

// Group Pricing Requests by Status Groups
export type VehiclePricingRequestStatusGroup = "requested" | "received";

export const groupVehiclePricingRequested = (
  requests: VehiclePricingRequest[]
): Record<VehiclePricingRequestStatusGroup, VehiclePricingRequest[]> => {
  const requested = requests.filter((request) => !request.pricingReceived);
  const received = requests.filter((request) => request.pricingReceived);

  return {
    requested: sortBy(requested, "requestSentDate", "desc"),
    received: sortBy(received, "pricingReceivedDate", "desc"),
  };
};

export const calculatePricingRequestAverages = (
  requests: VehiclePricingRequest[]
): VehiclePricingRequestAggregations => {
  const avgOnroadPrice = getAverageForObjectArray(
    requests,
    "onroadPrice",
    true
  );
  const avgDiscount = getAverageForObjectArray(requests, "discount", true);
  const { min: minDiscount, max: maxDiscount } = getMinMaxForObjectArray(
    requests,
    "discount",
    true
  );

  return {
    avgOnroadPrice,
    avgDiscount,
    minDiscount,
    maxDiscount,
  };
};
